import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { SEO, generateLocalServiceSchema } from '../components/SEO';
import { CTASection } from '../components/CTASection';
import { GoogleMapsSection } from '../components/GoogleMapsSection';
import { FAQSection } from '../components/FAQSection';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { ServiceCard } from '../components/ServiceCard';
import { Breadcrumb } from '../components/Breadcrumb';
import { TrustSection } from '../components/TrustBuilding';
import { services, neighborhoods, faqs, beforeAfterGallery } from '../data/mock';
import { MapPin, Navigation, ArrowRight, ExternalLink } from 'lucide-react';
import { slugify, getNearbyNeighborhoods, getCityName, getServiceTypeFromSlug } from '../utils/seoHelpers';
import { getLocalizedContent, detectCity, generateMetaDescription, generateH1 } from '../utils/localSEO';

export const LocalServicePage = () => {
  const locationHook = useLocation();
  
  // Extract location from pathname
  const pathname = locationHook?.pathname || '/lava-jato-goiania';
  const pathSegments = pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1] || 'lava-jato-goiania';
  
  // Use the full last segment as location
  const safeLocation = lastSegment || 'lava-jato-goiania';
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [safeLocation]);

  // Helper function to safely split and capitalize
  const slugToTitle = (slug) => {
    if (!slug || typeof slug !== 'string') return 'Goiânia';
    return slug
      .split('-')
      .map(word => {
        if (!word) return '';
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .filter(Boolean)
      .join(' ');
  };

  // Determina o tipo de serviço
  const isLavaJato = safeLocation?.includes('lava-jato') ?? false;
  const isEstetica = safeLocation?.includes('estetica-automotiva') ?? false;
  const isLavagemCompleta = safeLocation?.includes('lavagem-completa') ?? false;
  const isLavagemDetalhada = safeLocation?.includes('lavagem-detalhada') ?? false;
  const isDetalhamento = safeLocation?.includes('detalhamento-automotivo') ?? false;
  
  // Remove prefixo do slug para pegar apenas o bairro/cidade
  let cleanLocation = safeLocation || 'goiania';
  
  const prefixes = [
    'lava-jato-',
    'estetica-automotiva-',
    'lavagem-completa-',
    'lavagem-detalhada-',
    'detalhamento-automotivo-'
  ];
  
  for (const prefix of prefixes) {
    if (cleanLocation?.includes(prefix)) {
      cleanLocation = cleanLocation.replace(prefix, '');
      break;
    }
  }
  
  if (!cleanLocation || cleanLocation === '') {
    cleanLocation = 'goiania';
  }
  
  const neighborhoodDisplay = slugToTitle(cleanLocation);

  // Determine service type
  let serviceType = getServiceTypeFromSlug(safeLocation);
  
  // Detect actual city for this neighborhood
  const actualCity = detectCity(slugify(neighborhoodDisplay));
  
  // Generate unique content based on neighborhood
  const neighborhoodHash = slugify(neighborhoodDisplay).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const contentIndex = neighborhoodHash % 4;
  const localContent = getLocalizedContent(neighborhoodDisplay, actualCity, serviceType, contentIndex);
  
  // Generate unique H1 and meta
  const uniqueH1 = generateH1(serviceType, neighborhoodDisplay);
  const uniqueMetaDescription = generateMetaDescription(serviceType, neighborhoodDisplay, actualCity);
  const uniqueMetaTitle = `${serviceType} ${neighborhoodDisplay} ${actualCity} | Box7go`;

  // Get nearby neighborhoods using SEO helper
  const nearbyNeighborhoods = getNearbyNeighborhoods(neighborhoodDisplay, neighborhoods);
  
  // Get city info
  const cityName = actualCity;
  const citySlug = slugify(cityName);
  
  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Lava Jato Perto de Mim', link: '/lava-jato-perto-de-mim' },
    { label: cityName, link: isLavaJato ? `/lava-jato-${citySlug}` : `/estetica-automotiva-${citySlug}` },
    { label: uniqueH1 }
  ];

  const schemaData = generateLocalServiceSchema(
    neighborhoodDisplay,
    cityName,
    serviceType,
    {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "50",
        "bestRating": "5",
        "worstRating": "1"
      },
      "serviceType": [
        "Lavagem automotiva",
        "Estética automotiva",
        "Lavagem detalhada",
        "Detalhamento automotivo",
        "Polimento automotivo"
      ]
    }
  );

  const getDescription = () => {
    if (isEstetica || isDetalhamento) {
      return `Serviços especializados de estética automotiva em ${neighborhoodDisplay}. Polimento, vitrificação, detalhamento e muito mais para deixar seu veículo impecável.`;
    }
    return `Procurando lava jato de qualidade em ${neighborhoodDisplay}? A Box7go oferece serviços completos de lavagem automotiva com galpão coberto, produtos profissionais e equipe especializada.`;
  };

  // Related service pages
  const relatedServices = isLavaJato 
    ? services.filter(s => s.slug.includes('lavagem'))
    : services.filter(s => s.slug.includes('estetica') || s.slug.includes('detalhada'));
  
  // Main service page link
  const mainServiceSlug = isLavaJato ? 'lavagem-completa' : 'estetica-automotiva';

  return (
    <>
      <SEO 
        title={uniqueMetaTitle}
        description={uniqueMetaDescription}
        keywords={`${serviceType?.toLowerCase() || 'lava jato'} ${neighborhoodDisplay?.toLowerCase()}, ${serviceType?.toLowerCase()} em ${neighborhoodDisplay}, ${serviceType?.toLowerCase()} ${cityName?.toLowerCase()}, lavagem automotiva ${neighborhoodDisplay?.toLowerCase()}`}
        schemaData={schemaData}
      />

      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Hero */}
      <section className="relative min-h-[450px] flex items-center bg-black">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1731986799546-d18eb1110df4)`,
            opacity: 0.4
          }}
        ></div>
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-[#00B4D8]" />
              <span className="text-white/90 font-medium">{neighborhoodDisplay} - {cityName}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {uniqueH1}
            </h1>
            <p className="text-xl text-white/90 mb-4">
              {isLavaJato ? (
                <>Procurando <strong>lava jato em {neighborhoodDisplay}</strong>? A Box7go {localContent.intro}. Atendemos toda região de {cityName} com qualidade garantida.</>
              ) : (
                <>Serviços especializados de <strong>estética automotiva em {neighborhoodDisplay}</strong>. A Box7go {localContent.intro} para moradores de {cityName}.</>
              )}
            </p>
            <p className="text-lg text-white/80 mb-8">
              {localContent.coverage}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <WhatsAppButton 
                text={`Solicitar Orçamento em ${neighborhoodDisplay}`}
                className="text-lg px-8 py-4"
              />
              <a
                href="https://maps.app.goo.gl/u4Shanyp2XZS3BHZ8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-black font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105"
              >
                <Navigation className="w-5 h-5" />
                Como Chegar
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Por que escolher a Box7go - With Local SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Por que somos a melhor opção de {serviceType} em {neighborhoodDisplay}?
            </h2>
            <p className="text-lg text-gray-600 text-center mb-8 max-w-2xl mx-auto">
              {localContent.differential}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-bold text-xl text-gray-900 mb-3">🏠 Galpão Coberto</h3>
                <p className="text-gray-700">
                  Seu veículo protegido do sol e intempéries durante todo o serviço em {neighborhoodDisplay}. {isLavaJato ? 'Nossa estrutura garante lavagem de qualidade' : 'Ambiente ideal para detalhamento profissional'} independente do clima.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-bold text-xl text-gray-900 mb-3">✨ Produtos Premium</h3>
                <p className="text-gray-700">
                  Utilizamos apenas produtos automotivos de alta eficiência e qualidade profissional. {isLavaJato ? 'Lavagem que protege a pintura' : 'Polimento e cristalização com produtos importados'} para garantir durabilidade.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-bold text-xl text-gray-900 mb-3">👨‍🔧 Equipe Especializada</h3>
                <p className="text-gray-700">
                  Profissionais extremamente capacitados e experientes em {serviceType.toLowerCase()}. Nossa equipe atende {neighborhoodDisplay} com técnicas modernas e acabamento impecável.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-bold text-xl text-gray-900 mb-3">📍 Localização Estratégica</h3>
                <p className="text-gray-700">
                  Fácil acesso para moradores de {neighborhoodDisplay} e bairros próximos em {cityName}. Localização privilegiada com estacionamento e atendimento rápido.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Serviços - Optimized H2/H3 */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Serviços de {serviceType} em {neighborhoodDisplay}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Conheça todos os serviços disponíveis para moradores de {neighborhoodDisplay} e região de {cityName}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.slice(0, 6).map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">
              Todos os nossos serviços estão disponíveis para clientes de {neighborhoodDisplay}
            </p>
            <WhatsAppButton 
              text={`Agendar Serviço em ${neighborhoodDisplay}`}
              messageType="neighborhood"
              neighborhood={neighborhoodDisplay}
              variant="large"
            />
          </div>
        </div>
      </section>

      {/* Trust Building Section */}
      <TrustSection neighborhood={neighborhoodDisplay} serviceType={serviceType} />

      {/* Antes e Depois */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Resultados que Impressionam
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Veja a transformação que fazemos em veículos de {neighborhoodDisplay}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {beforeAfterGallery.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="relative">
                    <img 
                      src={item.before} 
                      alt={`${item.title} - Antes`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute bottom-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                      ANTES
                    </div>
                  </div>
                  <div className="relative">
                    <img 
                      src={item.after} 
                      alt={`${item.title} - Depois`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                      DEPOIS
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-gray-900 text-sm">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Atendemos também bairros próximos - New Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Atendemos também bairros próximos a {neighborhoodDisplay}
            </h2>
            <p className="text-lg text-gray-600 text-center mb-8">
              Além de {neighborhoodDisplay}, a Box7go oferece serviços de {serviceType.toLowerCase()} para moradores de diversos bairros em {cityName} e região metropolitana.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {nearbyNeighborhoods.map((neighborhood) => (
                <Link
                  key={neighborhood}
                  to={isLavaJato ? `/lava-jato-${slugify(neighborhood)}` : `/estetica-automotiva-${slugify(neighborhood)}`}
                  className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg hover:bg-[#00B4D8] hover:text-white transition-all duration-300 group shadow-sm hover:shadow-md"
                >
                  <MapPin className="w-4 h-4 flex-shrink-0 text-[#00B4D8] group-hover:text-white" />
                  <span className="text-sm font-medium">{neighborhood}</span>
                </Link>
              ))}
            </div>
            <div className="bg-gradient-to-r from-[#00B4D8]/10 to-[#0096C7]/10 rounded-xl p-6 md:p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                {isLavaJato ? 'Lava Jato' : 'Estética Automotiva'} em {cityName}
              </h3>
              <p className="text-gray-700 text-center mb-6">
                Nossa localização estratégica permite atender rapidamente moradores de {neighborhoodDisplay}, {nearbyNeighborhoods.slice(0, 2).join(', ')} e toda região de {cityName}. {localContent.differential}
              </p>
              <div className="flex justify-center">
                <WhatsAppButton 
                  text={localContent.cta}
                  className="text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bairros Próximos Atendidos - SEO Enhanced */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bairros Próximos Atendidos
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Oferecemos {serviceType.toLowerCase()} com a mesma qualidade premium em toda região próxima a {neighborhoodDisplay}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {nearbyNeighborhoods.map((neighborhood) => (
              <Link
                key={neighborhood}
                to={isLavaJato ? `/lava-jato-${slugify(neighborhood)}` : `/estetica-automotiva-${slugify(neighborhood)}`}
                className="flex items-center gap-2 p-4 bg-white rounded-lg hover:bg-[#00B4D8] hover:text-white transition-all duration-300 group shadow-md hover:shadow-lg"
              >
                <MapPin className="w-5 h-5 flex-shrink-0 text-[#00B4D8] group-hover:text-white" />
                <span className="text-sm font-medium">{neighborhood}</span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/lava-jato-perto-de-mim"
              className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold hover:text-[#0096C7] transition-colors text-lg"
            >
              Ver todos os bairros atendidos em {cityName}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Serviços Disponíveis Nesta Região - SEO Enhanced */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Serviços Disponíveis em {neighborhoodDisplay}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Todos os nossos serviços de {serviceType.toLowerCase()} estão disponíveis para moradores de {neighborhoodDisplay} e bairros próximos em {cityName}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {relatedServices.slice(0, 6).map((service) => (
              <Link
                key={service.id}
                to={`/${service.slug}`}
                className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="relative h-40">
                  <img 
                    src={service.image} 
                    alt={`${service.name} em ${neighborhoodDisplay}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-bold text-white text-lg">{service.name}</h3>
                    <p className="text-white/80 text-xs mt-1">Disponível em {neighborhoodDisplay}</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-3">{service.shortDesc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Navigation className="w-3 h-3" />
                      {service.duration}
                    </span>
                    <span className="inline-flex items-center gap-2 text-[#00B4D8] group-hover:text-[#0096C7] font-semibold text-sm">
                      Ver detalhes
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">
              Atendemos {neighborhoodDisplay} com todos os nossos serviços de {serviceType.toLowerCase()}
            </p>
            <Link
              to={`/${mainServiceSlug}`}
              className="inline-flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0096C7] text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 hover:scale-105"
            >
              Ver serviço completo de {serviceType} em {cityName}
              <ExternalLink className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Veja Também - Cross-linking SEO */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Veja Também
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Outros serviços e regiões atendidas pela Box7go
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Link to main city page */}
            <Link
              to={isLavaJato ? `/lava-jato-${citySlug}` : `/estetica-automotiva-${citySlug}`}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h3 className="font-bold text-xl text-gray-900 mb-2">{serviceType} {cityName}</h3>
              <p className="text-gray-600 mb-4">
                Confira todos os bairros atendidos em {cityName}
              </p>
              <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold">
                Saiba mais
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            {/* Link to opposite service type */}
            <Link
              to={isLavaJato ? `/estetica-automotiva-${slugify(neighborhoodDisplay)}` : `/lava-jato-${slugify(neighborhoodDisplay)}`}
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h3 className="font-bold text-xl text-gray-900 mb-2">
                {isLavaJato ? 'Estética Automotiva' : 'Lava Jato'} {neighborhoodDisplay}
              </h3>
              <p className="text-gray-600 mb-4">
                Conheça nossos serviços de {isLavaJato ? 'estética automotiva' : 'lavagem'} em {neighborhoodDisplay}
              </p>
              <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold">
                Ver serviços
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            {/* Link to near me page */}
            <Link
              to="/lava-jato-perto-de-mim"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h3 className="font-bold text-xl text-gray-900 mb-2">Encontre a Box7go</h3>
              <p className="text-gray-600 mb-4">
                Veja todos os bairros atendidos e encontre o mais próximo de você
              </p>
              <span className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold">
                Ver mapa
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Mapa */}
      <GoogleMapsSection />

      {/* FAQ */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <CTASection 
        title={`Agende seu serviço em ${neighborhoodDisplay}`}
        subtitle="Entre em contato pelo WhatsApp e garanta qualidade premium"
      />
    </>
  );
};
